import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { PeptideCard } from "@/components/PeptideCard";
import { BookOpen, Filter } from "lucide-react";
import { PageNavigation, PageBreadcrumb } from "@/components/PageNavigation";

// Peptide data - compound names not translated
const PEPTIDES_DATA = [
  {
    id: 1,
    name: "Retatrutide",
    label: "RETA-15",
    tags: ["Weight Loss", "Metabolic Health", "Physique"],
    translationKey: "retatrutide",
    dosage: "2 mg once weekly, increase slowly.",
  },
  {
    id: 2,
    name: "Tirzepatide",
    label: "TIRZ-30",
    tags: ["Weight Loss", "Metabolic Health", "Physique"],
    translationKey: "tirzepatide",
    dosage: "2.5 mg once weekly, titrate every 4 weeks.",
  },
  {
    id: 3,
    name: "Ipamorelin",
    label: "IPA-10",
    tags: ["Recovery", "Sleep & Hormones", "Physique"],
    translationKey: "ipamorelin",
    dosage: "200 mcg per day (often before bed).",
  },
  {
    id: 4,
    name: "Tesamorelin",
    label: "TESA-10",
    tags: ["Metabolic Health", "Sleep & Hormones", "Physique"],
    translationKey: "tesamorelin",
    dosage: "1.28 mg daily.",
  },
  {
    id: 5,
    name: "NAD+",
    label: "NAD+500",
    tags: ["Energy & Endurance", "Longevity", "Recovery"],
    translationKey: "nad",
    dosage: ["SC/IM: 20–50 mg per injection", "IV: ~250 mg per session"],
  },
  {
    id: 6,
    name: "KLOW (blend)",
    label: "KLOW-80",
    tags: ["Recovery", "Skin & Hair", "Longevity"],
    translationKey: "klow",
    dosage: "0.5 mg per day (total blend).",
  },
  {
    id: 7,
    name: "GLOW (blend)",
    label: "GLOW-70",
    tags: ["Skin & Hair", "Recovery"],
    translationKey: "glow",
    dosage: "0.5 mg per day (total blend).",
  },
  {
    id: 8,
    name: "SS-31 (Elamipretide)",
    label: "SS-31",
    tags: ["Energy & Endurance", "Longevity"],
    translationKey: "ss31",
    dosage: "5–10 mg daily.",
  },
  {
    id: 9,
    name: "MOTS-C",
    label: "MOTS-c",
    tags: ["Metabolic Health", "Energy & Endurance", "Longevity"],
    translationKey: "motsc",
    dosage: "5 mg, 2–3× per week.",
  },
  {
    id: 10,
    name: "TB-500",
    label: "TB-500",
    tags: ["Recovery", "Physique"],
    translationKey: "tb500",
    dosage: "2 mg per week (often split).",
  },
  {
    id: 11,
    name: "GHK-Cu",
    label: "GHK-Cu",
    tags: ["Skin & Hair", "Recovery"],
    translationKey: "ghkcu",
    dosage: ["Topical: 0.1–0.5% daily", "Injectable: ~1 mg per week"],
  },
];

// Tag mapping for translations
const TAG_KEYS = {
  "All": "all",
  "Weight Loss": "weightLoss",
  "Physique": "physique",
  "Recovery": "recovery",
  "Sleep & Hormones": "sleepHormones",
  "Energy & Endurance": "energyEndurance",
  "Longevity": "longevity",
  "Skin & Hair": "skinHair",
  "Metabolic Health": "metabolicHealth"
};

// All available tags
const ALL_TAGS = [
  "All",
  "Weight Loss",
  "Physique",
  "Recovery",
  "Sleep & Hormones",
  "Energy & Endurance",
  "Longevity",
  "Skin & Hair",
];

export const LearnPeptides = () => {
  const { t } = useTranslation();
  const [activeTag, setActiveTag] = useState("All");

  // Filter peptides based on selected tag
  const filteredPeptides = useMemo(() => {
    if (activeTag === "All") {
      return PEPTIDES_DATA;
    }
    return PEPTIDES_DATA.filter((peptide) => peptide.tags.includes(activeTag));
  }, [activeTag]);

  // Get translated tag name
  const getTagName = (tag) => {
    const key = TAG_KEYS[tag];
    return key ? t(`learn.tags.${key}`) : tag;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Breadcrumb Navigation */}
      <PageBreadcrumb currentPath="/learn" />

      {/* Page Header */}
      <div className="w-full py-8 px-4 border-b border-border/50 bg-gradient-to-b from-muted/30 to-background">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-primary rounded-lg">
              <BookOpen className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {t('learn.title')}
            </h1>
          </div>
          <p className="text-muted-foreground max-w-2xl">
            {t('learn.subtitle')}
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-muted/20 border-b border-border/50 py-4 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">{t('learn.filterByCategory')}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {ALL_TAGS.map((tag) => (
              <Button
                key={tag}
                variant={activeTag === tag ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTag(tag)}
                className={`
                  transition-all duration-200
                  ${activeTag === tag 
                    ? "bg-primary text-primary-foreground shadow-md" 
                    : "hover:bg-primary/10 hover:border-primary/50"
                  }
                `}
              >
                {getTagName(tag)}
                {tag !== "All" && (
                  <span className={`ml-1.5 text-xs ${activeTag === tag ? "opacity-80" : "opacity-50"}`}>
                    ({PEPTIDES_DATA.filter(p => p.tags.includes(tag)).length})
                  </span>
                )}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Peptides List */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('learn.showing')} <span className="font-semibold text-foreground">{filteredPeptides.length}</span> {filteredPeptides.length !== 1 ? t('learn.peptidesCount') : t('learn.peptideCount')}
            {activeTag !== "All" && (
              <span> {t('learn.in')} <span className="font-medium text-primary">{getTagName(activeTag)}</span></span>
            )}
          </p>
        </div>

        <div className="space-y-4">
          {filteredPeptides.map((peptide) => (
            <PeptideCard key={peptide.id} peptide={peptide} />
          ))}
        </div>

        {filteredPeptides.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('learn.noPeptides')}</p>
          </div>
        )}
      </div>

      {/* Page Navigation */}
      <PageNavigation currentPath="/learn" />

      {/* Disclaimer Footer */}
      <div className="border-t border-border/50 bg-muted/20 py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <strong>{t('common.disclaimer')}</strong> {t('common.disclaimerText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LearnPeptides;
